import styled, { css } from "styled-components";

const glassCardBorder = css`
	isolation: isolate;

	&::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		border: 1px solid #ffffff;
		mix-blend-mode: overlay;
		pointer-events: none;
		z-index: 2;
	}
`;

export const BettoLuckyTrigger = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	flex: 0 0 auto;
	flex-shrink: 0;
	align-self: center;
	box-sizing: border-box;
	width: max-content;
	max-width: max-content;
	height: 38px;
	padding: 0 12px;
	border: none;
	border-radius: 10px;
	background: transparent;
	color: #abb4cf;
	font-family: inherit;
	font-size: 14px;
	font-weight: 400;
	line-height: 1;
	cursor: pointer;
	white-space: nowrap;
	transition:
		background 0.2s ease,
		color 0.2s ease;

	&:hover:not(:disabled) {
		background: #0362d4;
		color: #e5f0fe;
	}

	span {
		display: inline-flex;
		align-items: center;
		line-height: 1;
	}

	svg {
		display: block;
		flex-shrink: 0;
		width: 14px;
		height: 14px;
	}
`;

export const BettoLuckyRoot = styled.div`
	position: fixed;
	inset: 0;
	z-index: 10000;
	display: ${({ $open }) => ($open ? "flex" : "none")};
	align-items: center;
	justify-content: center;
	padding: 16px;
	box-sizing: border-box;
`;

export const BettoLuckyOverlay = styled.button`
	position: absolute;
	inset: 0;
	border: none;
	padding: 0;
	background: rgba(5, 8, 16, 0.97);
	backdrop-filter: blur(8px);
	cursor: pointer;
`;

export const BettoLuckyPanel = styled.div`
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 420px;
	max-height: calc(100vh - 32px);
	padding: 24px 20px 20px;
	border-radius: 16px;
	border: 1px solid #343946;
	background: #171c2a;
	box-sizing: border-box;
	overflow-y: auto;
	${glassCardBorder}

	@media (min-width: 640px) {
		max-width: 560px;
		padding: 28px 28px 24px;
	}

	@media (min-width: 900px) {
		max-width: 640px;
	}
`;

export const BettoLuckyHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 20px;
`;

export const BettoLuckyTitle = styled.h2`
	margin: 0;
	color: #e5f0fe;
	font-family: var(--bt-font-family);
	font-size: 20px;
	font-weight: 600;
	line-height: 1.15;
	letter-spacing: -0.6px;
	text-transform: capitalize;

	@media (min-width: 640px) {
		font-size: 22px;
		letter-spacing: -0.72px;
	}
`;

export const BettoLuckyCloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 34px;
	height: 34px;
	padding: 8px;
	border: none;
	border-radius: 10px;
	background: #2b3245;
	color: #e5f0fe;
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}
`;

export const BettoLuckyBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;

	@media (min-width: 640px) {
		flex-direction: row;
		align-items: stretch;
		gap: 24px;
	}
`;

export const BettoLuckyImageWrap = styled.div`
	position: relative;
	flex-shrink: 0;
	width: 100%;
	max-width: 280px;
	margin: 0 auto;
	aspect-ratio: 1 / 1;
	border-radius: 14px;
	overflow: hidden;
	background: #1f2536;
	${glassCardBorder}

	@media (min-width: 640px) {
		width: 220px;
		max-width: none;
		margin: 0;
	}

	@media (min-width: 900px) {
		width: 244px;
	}

	img {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

export const BettoLuckyDetails = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	justify-content: center;
	gap: 12px;
	min-width: 0;
`;

export const BettoLuckyAffiliate = styled.span`
	color: #858faf;
	font-family: var(--bt-font-family);
	font-size: 12px;
	font-weight: 500;
	line-height: 1.2;
	letter-spacing: 0.2px;
	text-transform: uppercase;
`;

export const BettoLuckyGameName = styled.h3`
	margin: 0;
	color: #e5f0fe;
	font-family: var(--bt-font-family);
	font-size: 18px;
	font-weight: 600;
	line-height: 1.25;
	letter-spacing: -0.36px;
	word-break: break-word;

	@media (min-width: 640px) {
		font-size: 20px;
	}
`;

export const BettoLuckyActions = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	width: 100%;
	margin-top: 8px;

	@media (min-width: 640px) {
		margin-top: 12px;
	}
`;

const luckyButtonBase = css`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	min-height: 44px;
	padding: 0 16px;
	border-radius: 10px;
	font-family: var(--bt-font-family);
	font-size: 14px;
	font-weight: 600;
	line-height: 1;
	cursor: pointer;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		opacity 0.2s ease;

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	svg {
		display: block;
		flex-shrink: 0;
		width: 16px;
		height: 16px;
	}
`;

export const BettoLuckyPlayButton = styled.button`
	${luckyButtonBase}
	border: none;
	background: #0362d4;
	color: #e5f0fe;

	&:hover:not(:disabled) {
		background: #0470ef;
	}
`;

export const BettoLuckyTryAgainButton = styled.button`
	${luckyButtonBase}
	border: 1px solid #343946;
	background: #2b3245;
	color: #e5f0fe;

	&:hover:not(:disabled) {
		background: #343946;
		border-color: #4a5163;
	}
`;

export const BettoLuckyLoaderWrap = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 240px;
	width: 100%;
`;
